/**
 * The original's sprite sizing rule, as pure arithmetic: how big a `.TBL`
 * sprite node has to be *in metres* for it to cover the same patch of screen
 * the engine's 1:1 blit covers, and which LOD of a sprite object the engine
 * would have picked at that distance.
 *
 * ## The rule
 *
 * docs/formats/models-tbl.md §4.3: both sprite handlers "look the frame up,
 * project an anchor, then blit the frame at **1:1 pixels** with its top-left
 * at `projected + (offsetX, offsetY)`. There is **no perspective scaling** —
 * distance is expressed by which LOD, hence which frame, the engine picks."
 *
 * So an `n`-pixel-tall frame covers `n / 200` of the viewport height at every
 * range, and the world-space height a camera-facing quad needs to do that is
 * whatever the perspective divide undoes. For a `PerspectiveCamera` of
 * vertical field of view `fovY` rendering into `H` device pixels, a quad of
 * world height `h` at view-space depth `d` covers
 *
 * ```
 *   pixels = h / (2 · d · tan(fovY/2)) · H
 * ```
 *
 * and the target is `pixels = n · H / 200` — the frame blitted 1:1 into a
 * 200-line framebuffer presented at the viewport's height. Solving:
 *
 * ```
 *   h = n · 2 · d · tan(fovY/2) / 200
 * ```
 *
 * **`H` cancels.** That is worth stating plainly, because the natural way to
 * write this rule is "scale factor = viewportHeight/200" and that phrasing
 * invites a viewport-dependent bug of exactly the kind the 2026-08-19
 * gunfire-geometry lesson records ("check a screen-space derivation at three
 * window sizes, not one"). Here there is nothing to check at three window
 * sizes: the presentation scale and the projection's own pixel scale are the
 * same factor and divide out, so `metresPerScreenPixel` takes no viewport
 * argument at all and `sprite-screen-size.test.ts` pins that as an identity
 * rather than as four sampled sizes.
 *
 * Depth is the **view-space depth** (the component along the camera's forward
 * axis), not the euclidean range: a perspective divide is by `z`, so using the
 * range would shrink a sprite near the edge of a wide screen.
 *
 * ## Which LOD
 *
 * §3's `detail` is the apparent size in pixels at which the engine switches to
 * a LOD, and for the sprite objects it tracks the frame's own pixel size
 * closely (`SMOKE`'s twelve steps run `detail` 3→52 against frame widths
 * 8→56). `lodForApparentSize` therefore picks the highest LOD whose `detail`
 * the object's apparent size has reached, with LOD 0 as the floor — and
 * `apparentSizePx` measures that apparent size through the *same* projection
 * the sizing rule inverts, so the two cannot disagree about how far away
 * something is. The original's own field of view is not recorded anywhere in
 * the decode, so this is the port's camera FOV rather than a recovered
 * constant; the consequence is only which rung of a size ladder a burst is on.
 */

import { VGA_PIXEL_ASPECT } from "../flight/panel-layout.ts";
import type { BakedModelBillboard, BakedModelLodBody } from "./baked-model.ts";

/**
 * The original's framebuffer height — the denominator of the 1:1 blit rule,
 * the vertical counterpart of `polygon-outlines.ts`'s
 * `ORIGINAL_FRAMEBUFFER_WIDTH_PX`. Sprites are sized off the *height* because
 * a `PerspectiveCamera`'s field of view is vertical and its horizontal extent
 * follows the window's aspect, so height is the axis that is a property of the
 * camera rather than of the window.
 */
export const ORIGINAL_FRAMEBUFFER_HEIGHT_PX = 200;

/** Below this the perspective divide is meaningless; a sprite behind or on the lens is not drawn. */
const MIN_DEPTH_M = 1e-3;

/**
 * Metres of world size per one original sprite pixel, for a quad at view-space
 * depth `depthM` under a camera of vertical field of view `fovYRadians`. See
 * the module doc comment for the derivation and for why no viewport size is
 * needed. Returns 0 for a depth at or behind the lens, which collapses the
 * quad rather than inverting it.
 */
export function metresPerScreenPixel(depthM: number, fovYRadians: number): number {
  if (depthM <= MIN_DEPTH_M) return 0;
  return (2 * depthM * Math.tan(fovYRadians / 2)) / ORIGINAL_FRAMEBUFFER_HEIGHT_PX;
}

/**
 * The same, along the sprite's horizontal axis. A VGA mode-13h pixel is
 * **1.2× taller than it is wide** (`flight/panel-layout.ts`'s
 * `VGA_PIXEL_ASPECT`: 320×200 filled a 4:3 display), so a frame's displayed
 * shape is its pixel grid stretched vertically — equivalently, its horizontal
 * scale is the vertical one divided by 1.2. Applying the correction this way
 * round leaves the *vertical* rule above untouched, which is the one the
 * `H/200` derivation is stated in, and matches what the cockpit panel already
 * does to the original's own art (AD-009).
 */
export function metresPerScreenPixelX(depthM: number, fovYRadians: number): number {
  return metresPerScreenPixel(depthM, fovYRadians) / VGA_PIXEL_ASPECT;
}

/**
 * Apparent diameter, in original 200-line pixels, of something `radiusM`
 * across the radius at view-space depth `depthM` — the quantity §3's `detail`
 * thresholds are expressed in. The inverse of `metresPerScreenPixel` applied
 * to a diameter.
 */
export function apparentSizePx(radiusM: number, depthM: number, fovYRadians: number): number {
  const metresPerPixel = metresPerScreenPixel(depthM, fovYRadians);
  if (metresPerPixel <= 0) return Number.POSITIVE_INFINITY;
  return (2 * radiusM) / metresPerPixel;
}

/**
 * The inverse of `apparentSizePx`: how far away something `radiusM` across the
 * radius has to be to subtend `apparentPx` of a 200-line frame.
 *
 * This is the direction an *object* ladder needs. A sprite is handed a depth and
 * asked which rung it is on; a mesh ladder is handed the rungs and has to tell
 * three.js the **distances** they switch at, because `THREE.LOD` compares a
 * camera distance rather than an apparent size (`three/enhanced-lod.ts`). Same
 * projection, same constants, read the other way round, so the two cannot
 * disagree about how big something looks at a given range.
 *
 * `POSITIVE_INFINITY` for a threshold of zero — the floor rung, which every
 * distance is beyond.
 */
export function depthForApparentSize(radiusM: number, apparentPx: number, fovYRadians: number): number {
  if (apparentPx <= 0) return Number.POSITIVE_INFINITY;
  return (2 * radiusM * ORIGINAL_FRAMEBUFFER_HEIGHT_PX) / (2 * apparentPx * Math.tan(fovYRadians / 2));
}

/**
 * The LOD index §3's ladder selects for an apparent size: the highest rung
 * whose `detail` threshold has been reached, floored at rung 0 so something
 * further away than the least detailed step still draws. `details` is indexed
 * by LOD, in LOD order.
 */
export function lodForApparentSize(details: readonly number[], apparentPx: number): number {
  let chosen = 0;
  for (const [index, detail] of details.entries()) {
    if (apparentPx >= detail) chosen = index;
  }
  return chosen;
}

/** A sprite object's LOD ladder, in LOD order, as `lodForApparentSize` wants it. */
export function detailLadder(lods: readonly BakedModelLodBody[]): number[] {
  return [...lods].sort((a, b) => a.index - b.index).map((lod) => lod.detail ?? 0);
}

/**
 * The billboard a sprite object draws for one animation state at one LOD.
 *
 * §4.5's `stateArray[group.stateChannel]` walk is what makes an effect an
 * animation: the group's nodes are its successive instants, and the engine
 * draws the one the instance's state byte names. For `FLAK#0` the resulting
 * frame index is exactly `4·state + lod`, but that formula is a *property of
 * that object's data*, not a rule — `EXPLO#0` and the two small `FLAK`
 * variants reuse frames across the ladder — so this reads the frame out of the
 * baked node rather than computing it, and `sprite-screen-size.test.ts` pins
 * the `4·state + lod` identity against `FLAK-00`'s real bake.
 */
export function billboardForState(
  lod: BakedModelLodBody | undefined,
  stateIndex: number,
): BakedModelBillboard | undefined {
  return lod?.billboards?.find((billboard) => billboard.stateIndex === stateIndex);
}

/** How many distinct animation states a sprite object's LOD carries — the length of its state sequence. */
export function stateCountOf(lods: readonly BakedModelLodBody[]): number {
  let count = 0;
  for (const lod of lods) {
    for (const billboard of lod.billboards ?? []) count = Math.max(count, billboard.stateIndex + 1);
  }
  return count;
}
